// Help Center content - categories, articles, and FAQs
// Items with adminOnly: true are hidden from regular employees

export const categories = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: '🚀',
    articles: [
      {
        id: 'setup-workspace',
        title: 'Setting up your workspace',
        summary: 'Configure essential settings for your team',
        adminOnly: true,
        content: `
After installing Unplug from Slack, you'll want to configure a few key settings:

**1. Set your leave year**
Go to Settings → Workspace and choose when your leave year starts (usually January or April).

**2. Create departments**
Settings → Departments lets you organize your team. Each department can have:
- Designated approvers for leave requests
- Custom working days (e.g., different schedules for different teams)
- Specific leave entitlements

**3. Configure locations**
If you have offices in different countries, set up locations in Settings → Locations. This enables:
- Timezone-aware calculations
- Location-specific public holidays

**4. Add public holidays**
Go to Settings → Holidays to add your local public holidays. You can assign holidays to specific locations.

**5. Import your team**
Your team members are automatically imported from Slack. Check the Team page to ensure everyone has the correct department and role assigned.
        `,
        relatedSettings: '/settings'
      },
      {
        id: 'import-team',
        title: 'Importing your team from Slack',
        summary: 'How team members are synced from your Slack workspace',
        adminOnly: true,
        content: `
Unplug automatically syncs users from your Slack workspace:

**Automatic import**
- Active Slack members are imported when they first interact with Unplug
- Profile pictures, names, and emails are synced from Slack
- Guest users can be optionally included (Settings → Workspace)

**Manual import**
- Go to Team page and click "Import from Slack"
- Select which users to import
- Assign them to departments during import

**User roles**
After import, you can assign roles:
- **Owner**: Full access, billing management
- **Admin**: Manage settings, approve all leave
- **Approver**: Approve leave for assigned departments
- **Employee**: Request leave, view team calendar
        `,
        relatedSettings: '/team'
      },
      {
        id: 'configure-leave-types',
        title: 'Configuring leave types',
        summary: 'Customize vacation, sick leave, and more',
        adminOnly: true,
        content: `
Unplug comes with default leave types, but you can customize them:

**Default leave types**
- Vacation / Annual Leave
- Sick Leave
- Personal Day

**Creating custom leave types**
Go to Settings → Departments and edit any department's policies to:
- Add new leave types (e.g., Study Leave, Volunteer Day)
- Set custom entitlements per department
- Configure whether approval is required
- Choose colors for the calendar

**Leave type settings**
- **Unit**: Days or Hours
- **Approval required**: Auto-approve or require manager approval
- **Half-day allowed**: Enable half-day requests
- **Carryover**: How many days can carry to next year
- **Priority**: Higher priority leave overrides lower (e.g., sick overrides vacation)
- **Minimum duration**: Set a minimum required duration for requests (e.g., 4 hours or 1 day)
- **Maximum consecutive days**: Limit how many days can be taken at once
        `,
        relatedSettings: '/settings'
      },
      {
        id: 'using-unplug',
        title: 'Using Unplug',
        summary: 'Getting started as a team member',
        adminOnly: false,
        content: `
Welcome to Unplug! Here's how to get started:

**Requesting leave**
1. Go to the Requests page and click "New Request"
2. Select your leave type (Vacation, Sick Leave, etc.)
3. Choose your start and end dates
4. Add an optional note for your manager
5. Submit and wait for approval

**From Slack**
You can also request leave directly from Slack using the /unplug command or the Unplug Home tab.

**Viewing your balance**
- Your Dashboard shows your current leave balance
- See how many days you've used and how many remain

**Team calendar**
- Use the Planner to see when your colleagues are off
- The Calendar view shows leave and public holidays
        `,
        relatedSettings: '/requests'
      }
    ]
  },
  {
    id: 'leave-management',
    title: 'Leave Management',
    icon: '📅',
    articles: [
      {
        id: 'request-leave',
        title: 'Requesting leave',
        summary: 'How to submit time off requests',
        adminOnly: false,
        content: `
**From the Dashboard**
Click the "+" button and select "Request Leave", or go to Requests → New Request.

**From Slack**
Use the /unplug command or the Unplug Home tab to quickly request leave.

**Request details**
- Select leave type (Vacation, Sick, etc.)
- Choose start and end dates
- Optionally add a reason
- Half-day options available if enabled

**What happens next**
1. Your manager or department approver is notified
2. You'll receive a Slack message when approved/rejected
3. Your balance is updated automatically
        `,
        relatedSettings: '/requests'
      },
      {
        id: 'approve-requests',
        title: 'Approving leave requests',
        summary: 'How managers handle team requests',
        adminOnly: true,
        content: `
**Notification methods**
Approvers receive notifications via:
- Slack direct message with Approve/Reject buttons
- Email (if configured)
- Dashboard notification

**Approval options**
- **Approve**: Confirms the leave and updates balances
- **Reject**: Declines with optional reason
- **Edit**: Modify dates before approving (coming soon)

**Bulk approval**
On the Requests page, select multiple pending requests and approve them all at once.

**Auto-approval**
Some leave types (like sick leave) can be configured for auto-approval in Settings.
        `,
        relatedSettings: '/requests'
      },
      {
        id: 'manage-balances',
        title: 'Managing leave balances',
        summary: 'Understanding and adjusting team entitlements',
        adminOnly: true,
        content: `
**Balance calculation**
Available = Entitlement + Carried Over + Accrued - Used + Adjustment

**Viewing balances**
- Dashboard shows your personal balance
- Team page shows all team members' balances
- Settings → Opening Balances for admin management

**Adjusting balances**
Admins can manually adjust balances for:
- Bonus days
- Corrections
- Special circumstances

**Year initialization**
At the start of each leave year:
1. Go to Settings → Opening Balances
2. Click "Initialise Year"
3. Carryover is calculated automatically based on your policy
        `,
        relatedSettings: '/settings'
      },
      {
        id: 'understanding-balance',
        title: 'Understanding your balance',
        summary: 'How your leave balance is calculated',
        adminOnly: false,
        content: `
**Your leave balance**
Your available days are calculated as:
Entitlement + Carried Over - Used = Available

**What each term means**
- **Entitlement**: Your annual allocation (e.g., 25 days per year)
- **Carried Over**: Unused days from last year
- **Used**: Days you've already taken
- **Available**: Days remaining for you to use

**Checking your balance**
- View your balance on the Dashboard
- Click on the balance card for a detailed breakdown

**Running low on leave?**
Contact your manager or HR if you need additional days.
        `
      },
      {
        id: 'cancel-leave',
        title: 'Cancelling leave',
        summary: 'How to cancel an approved request',
        adminOnly: false,
        content: `
**Cancelling a request**
1. Go to the Requests page
2. Find your leave request
3. Click the cancel button (X)
4. Confirm the cancellation

**What happens when you cancel**
- Your leave balance is restored automatically
- Your manager is notified
- The calendar event is removed

**Note**
You can only cancel future leave. Past leave cannot be cancelled - speak to your manager for corrections.
        `
      }
    ]
  },
  {
    id: 'team-departments',
    title: 'Team & Departments',
    icon: '👥',
    adminOnly: true,
    articles: [
      {
        id: 'create-departments',
        title: 'Creating departments',
        summary: 'Organize your team into groups',
        adminOnly: true,
        content: `
**Why use departments?**
- Different teams can have different approvers
- Custom working hours per department
- Override leave entitlements per department
- Better organization in the Planner view

**Creating a department**
1. Go to Settings → Departments
2. Click "Add Department"
3. Enter name and configure settings
4. Assign approvers who can approve leave

**Department settings**
- Working days (Mon-Fri by default)
- Hours per day (8 hours default)
- Approver(s) for leave requests
- Notification channel for team updates
        `,
        relatedSettings: '/settings'
      },
      {
        id: 'setup-approvers',
        title: 'Setting up approvers',
        summary: 'Configure who approves leave requests',
        adminOnly: true,
        content: `
**Approval hierarchy**
Leave requests are routed based on:
1. User's direct manager (if set)
2. Department approvers (if no manager)

**Setting a user's manager**
- Go to Team page
- Edit the user
- Select their manager from the dropdown

**Department approvers**
- Go to Settings → Departments
- Edit the department
- Add one or more approvers
- All approvers are notified of requests

**Best practices**
- Always have at least 2 approvers per department for coverage
- Set direct managers for clearer accountability
- Use department approvers for backup
        `,
        relatedSettings: '/team'
      },
      {
        id: 'user-roles',
        title: 'User roles explained',
        summary: 'Understanding permissions and access levels',
        adminOnly: true,
        content: `
**Owner**
- Full access to all features
- Manage billing and subscription
- Cannot be removed (can only transfer ownership)

**Admin**
- Configure all settings
- Manage team members
- Approve any leave request
- View all data and reports

**Approver**
- Approve leave for assigned departments
- View team calendar
- Limited settings access

**Employee**
- Request leave
- View personal balances
- See team calendar (based on visibility settings)
        `,
        relatedSettings: '/team'
      }
    ]
  },
  {
    id: 'settings-config',
    title: 'Settings & Configuration',
    icon: '⚙️',
    adminOnly: true,
    articles: [
      {
        id: 'locations-holidays',
        title: 'Locations & public holidays',
        summary: 'Managing multi-location teams',
        adminOnly: true,
        content: `
**Setting up locations**
1. Go to Settings → Locations
2. Click "Add Location"
3. Set the country and timezone
4. Mark one as default

**Public holidays**
Each location can have its own public holidays:
1. Go to Settings → Holidays
2. Add holidays manually or import from a template
3. Assign to specific locations

**How holidays affect leave**
- Public holidays are shown on the calendar
- Leave requests spanning holidays are adjusted automatically
- Holiday deduction can be configured per workspace
        `,
        relatedSettings: '/settings'
      },
      {
        id: 'notifications',
        title: 'Notification settings',
        summary: 'Configure Slack alerts and summaries',
        adminOnly: true,
        content: `
**Available notifications**
- Leave request submitted
- Leave approved/rejected
- Daily summary of team absences
- Upcoming leave reminders

**Configuring notifications**
Go to Settings → Notifications to:
- Enable/disable notification types
- Set daily summary time
- Choose notification channels

**Channel notifications**
You can send notifications to specific Slack channels:
- Team-wide announcements
- Department-specific channels
- Private approver channels
        `,
        relatedSettings: '/settings'
      },
      {
        id: 'slack-status',
        title: 'Automatic Slack Status Updates',
        summary: 'Show your leave status in Slack automatically',
        adminOnly: false,
        content: `
**What is Slack Status Sync?**
When enabled, Unplug automatically updates your Slack status when you're on leave. For example, your status might show "🏖️ On Vacation until Feb 10" or "🤒 Out Sick".

**How to enable it**
1. Click your profile picture in the top navigation
2. Go to the **Settings** tab
3. Find "Slack Status Updates" and toggle it on

**Requirements**
This feature requires your Slack workspace owner to have installed Unplug with the correct permissions. If you see "Re-install app to enable", ask your workspace admin to reinstall the Unplug app from the Slack App Directory.

**What happens when enabled**
- Your Slack status is automatically set when your leave starts
- Status shows the leave type icon and return date
- Status is cleared when your leave ends
- Multi-day leave: status is refreshed daily at 8am
- Your previous status is restored after leave ends

**Privacy note**
Only your leave type and return date are shown in your Slack status. Specific details or reasons for your leave are never displayed.
        `,
        relatedSettings: '/profile?tab=settings'
      },
      {
        id: 'integrations',
        title: 'Calendar integrations',
        summary: 'Sync leave with Google Calendar or Outlook',
        adminOnly: true,
        content: `
**Supported integrations**
- Google Calendar
- Microsoft Outlook/365

**Setting up calendar sync**
1. Go to Settings → Integrations
2. Click "Connect" next to your calendar provider
3. Authorize access
4. Choose sync settings

**What gets synced**
- Approved leave creates calendar events
- Events are updated if leave is modified
- Cancelled leave removes calendar events

**Personal vs Team calendars**
Each user can connect their own calendar, or admins can set up a shared team calendar.
        `,
        relatedSettings: '/settings'
      },
      {
        id: 'bamboohr-integration',
        title: 'BambooHR Integration',
        summary: 'Import employees and time-off from BambooHR',
        adminOnly: true,
        content: `
**What is BambooHR integration?**
BambooHR integration allows you to import your employee directory and approved time-off requests from BambooHR into Unplug. This is a one-way sync that keeps your Unplug data in sync with your BambooHR system.

**Setting up the integration**
1. Go to Settings → Integrations
2. Click "Connect" next to BambooHR
3. Enter your BambooHR subdomain (e.g., "yourcompany" from yourcompany.bamboohr.com)
4. Enter your BambooHR API key (generate one from BambooHR → Account → API Keys)
5. Follow the setup wizard to map users and leave types

**Setup wizard steps**
- **Step 1 - Connect**: Enter your subdomain and API key
- **Step 2 - Map Users**: Match BambooHR employees to Unplug users by email
- **Step 3 - Map Leave Types**: Match BambooHR time-off types to your Unplug leave types
- **Step 4 - Review**: Confirm your mappings before importing
- **Step 5 - Import**: Watch as your data is imported

**What gets imported**
- Employee details are matched by email address
- Approved time-off requests are imported as leave in Unplug
- Leave balances are updated automatically
- Already-synced leave won't be duplicated

**Tips for a smooth import**
- Ensure employee emails match between BambooHR and Unplug
- Create all required leave types in Unplug before mapping
- Review the preview data before confirming the import
- Unmapped users or leave types will be skipped during import

**Generating a BambooHR API key**
1. Log into BambooHR as an admin
2. Click your name in the top right → API Keys
3. Click "Add New Key"
4. Give it a name (e.g., "Unplug Integration")
5. Copy the key and paste it into Unplug

**Re-syncing data**
You can run the sync again at any time from Settings → Integrations. Only new or changed time-off requests will be imported - existing synced records are tracked to prevent duplicates.
        `,
        relatedSettings: '/settings'
      }
    ]
  },
  {
    id: 'billing-plans',
    title: 'Billing & Plans',
    icon: '💳',
    adminOnly: true,
    articles: [
      {
        id: 'subscription-plans',
        title: 'Subscription plans',
        summary: 'Compare features and pricing',
        adminOnly: true,
        content: `
**Free Plan**
- Up to 5 users
- Basic leave tracking
- Public holidays
- Balance carryover

**Pro Plan**
- Up to 50 users
- Planner view
- Departments & policies
- Multiple locations
- Data export
- Calendar integrations

**Business Plan**
- Unlimited users
- All Pro features plus:
- Audit trail
- API access
- Advanced integrations
- Priority support
        `,
        relatedSettings: '/billing'
      },
      {
        id: 'upgrade-plan',
        title: 'Upgrading your plan',
        summary: 'How to upgrade and what to expect',
        adminOnly: true,
        content: `
**How to upgrade**
1. Go to Billing page
2. Click "Upgrade"
3. Choose your plan (monthly or yearly)
4. Enter payment details

**Yearly discount**
Save up to 20% with annual billing.

**What happens when you upgrade**
- Features are available immediately
- Existing data is preserved
- No disruption to your team
        `,
        relatedSettings: '/billing'
      }
    ]
  }
];

export const faqs = [
  {
    question: 'How do I request time off?',
    answer: 'Go to the Requests page and click "New Request". Select your leave type, choose dates, and submit. You can also use the Slack command /unplug to quickly request leave.',
    adminOnly: false
  },
  {
    question: 'How are leave balances calculated?',
    answer: 'Your available balance is calculated as: Entitlement + Carried Over - Used. View your full breakdown on the Dashboard.',
    adminOnly: false
  },
  {
    question: 'How do public holidays affect my leave balance?',
    answer: 'Public holidays are automatically excluded from leave calculations. If you request 5 days that include a public holiday, only 4 working days will be deducted from your balance.',
    adminOnly: false
  },
  {
    question: 'What happens when my leave spans two years?',
    answer: 'The system automatically splits the leave between years. For example, if you take leave from Dec 30 to Jan 3, days in December come from your current year balance, and January days from the new year.',
    adminOnly: false
  },
  {
    question: 'How do I cancel a leave request?',
    answer: 'Go to Requests, find your request, and click the cancel button. If already approved, cancellation will restore your balance automatically.',
    adminOnly: false
  },
  {
    question: 'Can I request half-day leave?',
    answer: 'Yes, if the leave type allows it. When creating a request, you\'ll see options for morning or afternoon half-days.',
    adminOnly: false
  },
  {
    question: 'How do I view my team\'s availability?',
    answer: 'Use the Planner (also called Wallchart) to see a visual overview of all team absences. The Calendar view also shows leave and public holidays.',
    adminOnly: false
  },
  {
    question: 'How do I automatically update my Slack status when on leave?',
    answer: 'Go to your Profile page (click your avatar in the top navigation), then select the Settings tab. Toggle on "Slack Status Updates". When enabled, your Slack status will automatically show that you\'re on leave with your return date. If you see "Re-install app to enable", ask your workspace admin to reinstall the Unplug app.',
    adminOnly: false
  },
  {
    question: 'How do I contact support?',
    answer: 'Click the chat button in the bottom-right corner of any page to send us a message. We typically respond within a few hours during business days.',
    adminOnly: false
  },
  {
    question: 'Can I set different entitlements for different departments?',
    answer: 'Yes! Go to Settings → Departments and edit department policies. You can override the default entitlement for any leave type per department.',
    adminOnly: true
  },
  {
    question: 'How do I set up approval workflows?',
    answer: 'Each user can have a direct manager (set in Team → Edit User). Alternatively, assign approvers to departments in Settings → Departments.',
    adminOnly: true
  },
  {
    question: 'Can I track leave in hours instead of days?',
    answer: 'Yes! When creating or editing a leave type, change the unit from "days" to "hours". Balances and requests will then use hours.',
    adminOnly: true
  },
  {
    question: 'How do I carry over unused leave to next year?',
    answer: 'Carryover happens automatically based on your leave type settings. Configure max carryover days in Settings → Departments → Edit Leave Type.',
    adminOnly: true
  },
  {
    question: 'Can I export my team\'s leave data?',
    answer: 'Yes, go to the Planner page and click Export. You can download data as CSV or Excel for payroll or reporting.',
    adminOnly: true
  },
  {
    question: 'How do I add a new team member?',
    answer: 'Team members are automatically synced from Slack. Once they\'re in your Slack workspace, they\'ll appear in Unplug. You can also manually import specific users from Team → Import from Slack.',
    adminOnly: true
  },
  {
    question: 'What\'s the difference between owner and admin roles?',
    answer: 'Owners have full access including billing management and can\'t be removed. Admins have full feature access but can\'t manage billing or remove owners.',
    adminOnly: true
  },
  {
    question: 'How do I sync leave with my calendar?',
    answer: 'Go to Settings → Integrations and connect your Google Calendar or Outlook. Approved leave will automatically create calendar events.',
    adminOnly: true
  },
  {
    question: 'Can I customize the leave types?',
    answer: 'Yes! Go to Settings → Departments, edit a department, and you can create custom leave types with specific entitlements, colors, and approval requirements.',
    adminOnly: true
  },
  {
    question: 'Can I set a minimum duration for leave requests?',
    answer: 'Yes! When editing a leave type, set the "Minimum Duration" field. For example, set it to 4 hours to require at least half-day requests, or 1 day to prevent hourly bookings. This works with both hours-based and days-based leave types.',
    adminOnly: true
  },
  {
    question: 'What happens if I exceed my leave balance?',
    answer: 'By default, requests that would result in a negative balance are allowed but flagged. Admins can configure strict enforcement in Settings if needed.',
    adminOnly: true
  },
  {
    question: 'How do I import data from BambooHR?',
    answer: 'Go to Settings → Integrations and click Connect next to BambooHR. Enter your BambooHR subdomain and API key, then follow the wizard to map employees and leave types. Approved time-off will be imported and balances updated automatically.',
    adminOnly: true
  },
  {
    question: 'Will my BambooHR data sync automatically?',
    answer: 'BambooHR integration is a one-way import from BambooHR to Unplug. You can run the sync manually from Settings → Integrations whenever you want to import new time-off requests. Already-synced leave is tracked to prevent duplicates.',
    adminOnly: true
  }
];
